import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {analytics} from '../../services/analytics';
import {logger, LogFlow} from '../../services/logger';
import {ehrService} from '../../services/ehrService';
import {
  buildPatientSearchQueries,
  normalizePatientSearchText,
  patientMatchesSearch,
} from '../../utils/patientSearch';

// NOTE on business hours: the backend is the single source of truth. It
// returns 503 when the pool window is closed (`AYDEN_LIVE_SEARCH_IGNORE_HOURS`
// controls this), and we surface that via `deepSearchUnavailable` below.
//
// NOTE on triggering: deep search is manual-only. Typing never hits
// Ayden. The caller must call `triggerDeepSearch` (wired to the
// "Search full EHR" button) to dispatch a fetch. Typing after a fetch
// lands still narrows the result set locally via the prefix cache.

// Deep search is manual-only: it fires exclusively when the user clicks
// the "Search full EHR" button surfaced by `EhrDeepSearchBanner`. The
// previous debounce-based auto-trigger was too easy to accidentally
// stampede (one Ayden call per keystroke whenever typing cadence drifted
// past the debounce window). Requiring an explicit click also guarantees
// the query is "fully baked" — no Ayden time wasted on partial names.
const DEEP_SEARCH_MIN_QUERY_LENGTH = 3;

// How many patients to pull per fetch. Larger cap = deeper prefix cache =
// more keystrokes narrowed locally without round-tripping Ayden.
const DEEP_SEARCH_LIMIT = 50;

// If Ayden hangs we bail client-side rather than leaving a spinner forever.
// Ayden runs are normally ~5s but have been observed stretching past 15s
// under load; 20s gives those slower runs a chance to land before we abort.
// Backend will eventually error too.
const DEEP_SEARCH_TIMEOUT_MS = 20000;

// How long a deep search can be in flight before we mark it "slow" so the
// picker can upgrade its status copy from "Searching full EHR…" to
// something a bit more patient.
const DEEP_SEARCH_SLOW_THRESHOLD_MS = 4000;

// When a user's search finds NOTHING in the cache, we auto-run one live EHR
// search so they don't have to discover the manual "Search full EHR" button
// (the real-world failure was a user staring at "no matches" for a patient
// who simply wasn't synced yet). Throttled so a fast typist who blanks the
// cache on several queries in a row can't stampede Ayden — at most one
// auto-dispatch per this interval, on top of the one-per-distinct-query guard.
const AUTO_DEEP_SEARCH_MIN_INTERVAL_MS = 2000;
const REMOTE_SEARCH_CACHE_TTL_MS = 2 * 60 * 1000;
const REMOTE_SEARCH_CACHE_LIMIT = 40;

const remoteSearchCache = new Map();
const remoteSearchPending = new Map();

function filterPatientsByQuery(patients: any, query: any) {
  return patients.filter((p: any) => patientMatchesSearch(query, p));
}

function dedupePatients(patients: any) {
  const byKey = new Map();
  for (const patient of patients) {
    const key =
      patient.id ||
      patient.mrn ||
      patient.accountNo ||
      patient.cacheDocumentId ||
      patient.ehrSourceId;
    byKey.set(key || `anon_${byKey.size}`, patient);
  }
  return [...byKey.values()];
}

function remoteSearchCacheKey(companyId: any, query: any) {
  return [companyId || '', normalizePatientSearchText(query)].join('\u0001');
}

function readRemoteSearchCache(key: string) {
  const cached = remoteSearchCache.get(key);
  if (!cached) return null;
  if (Date.now() - cached.cachedAt > REMOTE_SEARCH_CACHE_TTL_MS) {
    remoteSearchCache.delete(key);
    return null;
  }
  remoteSearchCache.delete(key);
  remoteSearchCache.set(key, cached);
  return cached.patients || [];
}

function writeRemoteSearchCache(key: string, patients: any[] = []) {
  if (!key) return;
  if (remoteSearchCache.has(key)) remoteSearchCache.delete(key);
  remoteSearchCache.set(key, {cachedAt: Date.now(), patients});
  while (remoteSearchCache.size > REMOTE_SEARCH_CACHE_LIMIT) {
    const oldestKey = remoteSearchCache.keys().next().value;
    if (!oldestKey) break;
    remoteSearchCache.delete(oldestKey);
  }
}

function notifyRemoteSearchListeners(entry: any) {
  const snapshot = [...entry.patients];
  entry.listeners.forEach((listener: any) => listener(snapshot));
}

function mergeRemoteSearchPatients(entry: any, patients: any[] = []) {
  if (!patients.length) return;
  entry.patients = dedupePatients([...entry.patients, ...patients]);
  notifyRemoteSearchListeners(entry);
}

async function searchRemoteEhrPatients(
  companyId: any,
  trimmedQuery: string,
  onPatients?: (patients: any[]) => void,
) {
  const key = remoteSearchCacheKey(companyId, trimmedQuery);
  const cached = readRemoteSearchCache(key);
  if (cached) {
    onPatients?.(cached);
    return cached;
  }

  const pending = remoteSearchPending.get(key);
  if (pending) {
    if (onPatients) {
      pending.listeners.add(onPatients);
      if (pending.patients.length) onPatients([...pending.patients]);
    }
    try {
      return await pending.promise;
    } finally {
      if (onPatients) pending.listeners.delete(onPatients);
    }
  }

  const entry = {
    listeners: new Set(onPatients ? [onPatients] : []),
    patients: [],
    promise: null as any,
  };
  remoteSearchPending.set(key, entry);

  const request = (async () => {
    const queries = buildPatientSearchQueries(trimmedQuery);
    const results = await Promise.allSettled([
      ...queries.map(q =>
        ehrService.fetchPatients(q, 100).then(result => {
          mergeRemoteSearchPatients(entry, result.patients || []);
          return result;
        }),
      ),
      ehrService.fetchCachedPatients(companyId, trimmedQuery).then(patients => {
        const result = {patients};
        mergeRemoteSearchPatients(entry, patients || []);
        return result;
      }),
    ]);
    const fulfilled = results
      .filter(result => result.status === 'fulfilled')
      .map(result => result.value);
    if (fulfilled.length === 0 && results.length > 0) {
      throw results.find(result => result.status === 'rejected')?.reason;
    }
    return entry.patients.length
      ? entry.patients
      : dedupePatients(fulfilled.flatMap(result => result.patients || []));
  })();

  entry.promise = request;
  try {
    const patients = await request;
    writeRemoteSearchCache(key, patients);
    return patients;
  } finally {
    remoteSearchPending.delete(key);
  }
}

// True for queries that are purely digits / date separators (MRN, accountNo,
// DOB in any common format). The Ayden `live-fetch-patient-name` workflow
// types into ECW's Name-mode patient lookup field, which is a last-name
// prefix matcher — it has no way to interpret a numeric / date string and
// will always return zero rows. The cached path indexes those fields in
// Firestore so the user sees those matches without us burning an Ayden
// run on a guaranteed-empty query.
function isNameShapedQuery(value: any) {
  const raw = String(value || '').trim();
  if (!raw) return false;
  return /[A-Za-z]/.test(raw);
}

// Coerce whatever the user typed into ECW-native `Last[, First]` form(s).
// Background: ECW's patient lookup field in Name mode is a `Last[, First]`
// prefix matcher with NO smart parsing — typing "Jose Gutierrez" returns
// zero rows because it asks "find rows where last name starts with
// 'Jose Gutierrez'". The only inputs that work are:
//
//   - "<last>"           — last-name prefix (e.g. "Gutierrez" → all G's)
//   - "<last>, <first>"  — exact `Last, First` match
//
// So our job is to dispatch one or two ECW-shaped queries and never send
// "First Last" or other free-text variants that always 0-out.
//
// Returns up to 2 query strings. Multiple variants are dispatched in
// parallel by the caller so 3+ token names that could be either
// "First Last…" or "First Last Compound" both find their patient.
function buildDeepSearchQueries(query: any) {
  const raw = String(query || '').trim();
  if (!raw || !isNameShapedQuery(raw)) return [];

  // Already in ECW format — pass through. ECW's `#searchText` field
  // parses the comma natively as `Last, First`.
  if (raw.includes(',')) return [raw];

  const tokens = raw.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return [];

  // Single token → last-name prefix. We can't tell if the user means a
  // first or last name; ECW only indexes last names in Name mode so this
  // is the only thing we can ask for.
  if (tokens.length === 1) return [raw];

  // Two tokens, no comma → assume "First Last". Rewrite to ECW form so
  // the search actually returns the named patient instead of nothing.
  if (tokens.length === 2) {
    return [`${tokens[1]}, ${tokens[0]}`];
  }

  // Three+ tokens, no comma — could be either:
  //   - "First Middle… Last"          → e.g. "Maria Elena Garcia"
  //   - "First Last_token1 Last_token2" → Spanish/Portuguese compound
  //                                       last names (common in CIFC's
  //                                       patient population), e.g.
  //                                       "Tania Garmendia Valenzuela"
  // We have no signal to disambiguate, so dispatch both shapes and let
  // dedup merge the results. Worst case one of the two returns zero.
  const last1 = tokens[tokens.length - 1];
  const first1 = tokens.slice(0, -1).join(' ');
  const last2 = `${tokens[tokens.length - 2]} ${tokens[tokens.length - 1]}`;
  const first2 = tokens.slice(0, -2).join(' ');
  return [`${last1}, ${first1}`, `${last2}, ${first2}`];
}

// ── Hook ──────────────────────────────────────────────────────────────

export default function useEhrPatientSearch({
  autoDeepSearch = true,
  autoDeepSearchMode = 'empty',
  companyId,
  hasActiveEhr,
  searchQuery,
  cachedPatients = [],
}: Record<string, any>) {
  // Cached (indexed) search state.
  const [remotePatients, setRemotePatients] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<any>(null);

  // Deep (live EHR) search state.
  const [deepSearchResults, setDeepSearchResults] = useState<any[]>([]);
  const [isDeepSearching, setIsDeepSearching] = useState(false);
  const [deepSearchSlow, setDeepSearchSlow] = useState(false);
  const [deepSearchUnavailable, setDeepSearchUnavailable] = useState(false);
  const [deepSearchError, setDeepSearchError] = useState<any>(null);
  // Persistent confirmation summary written when a deep search resolves
  // (success OR zero-result success). Callers use this to render
  // "Checked full EHR — N more match(es)" / "no new matches" / "no
  // matches found" so the user always gets clear feedback that the
  // network call ran, even when the result set already overlapped with
  // what the cache had. This intentionally does NOT cover error /
  // unavailable cases — those are surfaced via `deepSearchError` and
  // `deepSearchUnavailable` so the banner can render them with their
  // own iconography and copy.
  //
  // Shape: { at: <epochMs>, additionalCount: <int>, totalCount: <int> } | null
  const [deepSearchCompletion, setDeepSearchCompletion] = useState<any>(null);

  const requestIdRef = useRef(0);

  // Mirror of `remotePatients` accessible inside the deep-search async
  // callback. We need the LATEST cached result set when computing
  // `additionalCount` so we can tell the user how many of the deep hits
  // they couldn't already see.
  const remotePatientsRef = useRef<any[]>([]);
  useEffect(() => {
    remotePatientsRef.current = remotePatients;
  }, [remotePatients]);

  // Single-entry prefix cache. Invariant: `patients` is the full deep-search
  // response for `prefix` (under `companyId`). Queries that start with
  // `prefix` are narrowed locally with zero network cost.
  const deepCacheRef = useRef<{companyId: any; prefix: any; patients: any[]}>({
    companyId: null,
    prefix: null,
    patients: [],
  });

  // Currently in-flight deep fetch — tracked OUTSIDE the effect so it can
  // survive across re-renders. When the user extends the query while a
  // prior fetch is still running (typing "Luc" → "Luca" → "Lucas"), the
  // effect piggy-backs on this fetch instead of aborting and starting a
  // new one. The in-flight response is always fetched with `DEEP_SEARCH_LIMIT`
  // (50), so once it lands, the existing prefix-cache narrowing path
  // filters it down to whatever the user has typed by then.
  //
  // Shape: { companyId, query, controller, startedAt } | null
  const liveFetchRef = useRef<any>(null);

  // Always equals the latest trimmed query. The in-flight fetch success
  // path reads this to decide how to narrow the response — the user may
  // have typed more characters while the request was in the air.
  const latestQueryRef = useRef('');

  // Manual trigger for the "Search full EHR" escape hatch. Incrementing this
  // clears the prefix cache and forces the deep effect to refetch even if
  // the query text hasn't changed.
  const [manualTriggerTick, setManualTriggerTick] = useState(0);
  const manualTriggerPendingRef = useRef(false);

  // Records what kicked off the most recent deep search — 'manual' (the
  // "Search full EHR" button) or 'auto' (cache returned empty). Used purely
  // for PostHog attribution; both sources dispatch identically.
  const lastDeepTriggerSourceRef = useRef('manual');

  const triggerDeepSearch = useCallback((source = 'manual') => {
    deepCacheRef.current = {companyId: null, prefix: null, patients: []};
    manualTriggerPendingRef.current = true;
    // Button onClick passes a DOM event as the first arg; only honor an
    // explicit string source so a click never mislabels itself.
    lastDeepTriggerSourceRef.current =
      typeof source === 'string' ? source : 'manual';
    setManualTriggerTick(t => t + 1);
  }, []);

  // ── Cached search effect (indexed prefix + ranking) ────────────────
  useEffect(() => {
    const trimmedQuery = searchQuery?.trim() || '';
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;

    if (!hasActiveEhr || !companyId || !trimmedQuery) {
      setRemotePatients([]);
      setIsSearching(false);
      setSearchError(null);
      return undefined;
    }

    let cancelled = false;
    setIsSearching(true);

    const timeoutId = setTimeout(async () => {
      try {
        const patients = await searchRemoteEhrPatients(
          companyId,
          trimmedQuery,
          nextPatients => {
            if (cancelled || requestIdRef.current !== requestId) return;
            setRemotePatients(nextPatients);
            setSearchError(null);
          },
        );
        if (cancelled || requestIdRef.current !== requestId) return;
        setRemotePatients(patients);
        setSearchError(null);
      } catch (error) {
        if (cancelled || requestIdRef.current !== requestId) return;
        console.error('Failed to search EHR patients:', error);
        setRemotePatients([]);
        setSearchError(error);
      } finally {
        if (!cancelled && requestIdRef.current === requestId) {
          setIsSearching(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, [companyId, hasActiveEhr, searchQuery]);

  // ── Deep (live EHR) search effect ──────────────────────────────────
  //
  // Design: deep search is MANUAL-ONLY.
  //   - Typing alone never fires Ayden. On query changes we only try
  //     prefix-cache narrowing (free, synchronous) so previously-fetched
  //     results stay useful as the user refines their query.
  //   - Clicking "Search full EHR" (triggerDeepSearch) fires one call,
  //     immediately, with no debounce — the button press is the
  //     debounce. A fresh click wipes the cache and aborts any prior
  //     in-flight call, so rapid double-clicks don't pile up requests.
  //   - Hard 15s client timeout still applies so the spinner can't stick.
  //
  // The fetch lifecycle lives on `liveFetchRef`, not the effect closure,
  // so an in-flight response can still populate the cache and narrow
  // correctly if the user keeps typing after clicking.
  useEffect(() => {
    const trimmedQuery = searchQuery?.trim() || '';
    latestQueryRef.current = trimmedQuery;

    const meetsPreconditions =
      hasActiveEhr &&
      !!companyId &&
      trimmedQuery.length >= DEEP_SEARCH_MIN_QUERY_LENGTH;

    if (!meetsPreconditions) {
      if (liveFetchRef.current) {
        liveFetchRef.current.controller.abort();
        liveFetchRef.current = null;
      }
      setDeepSearchResults([]);
      setIsDeepSearching(false);
      setDeepSearchSlow(false);
      setDeepSearchUnavailable(false);
      setDeepSearchError(null);
      setDeepSearchCompletion(null);
      return undefined;
    }

    const isManualTrigger = manualTriggerPendingRef.current;
    manualTriggerPendingRef.current = false;

    if (!isManualTrigger) {
      // Query changed (typing) — never hit Ayden. Try local narrowing
      // only, and otherwise go idle so the banner can show the manual
      // trigger button.
      const live = liveFetchRef.current;
      const qLower = trimmedQuery.toLowerCase();

      // If a manual fetch is in flight for a prefix of the current
      // query (user typed more after clicking), keep the spinner up —
      // the response will narrow against `latestQueryRef` when it lands.
      if (
        live &&
        live.companyId === companyId &&
        qLower.startsWith(live.query.toLowerCase())
      ) {
        setIsDeepSearching(true);
        return undefined;
      }

      // Prefix cache narrowing (free).
      const cache = deepCacheRef.current;
      const cacheCoversQuery =
        cache.companyId === companyId &&
        typeof cache.prefix === 'string' &&
        cache.prefix.length > 0 &&
        qLower.startsWith(cache.prefix.toLowerCase());

      if (cacheCoversQuery) {
        const narrowed = filterPatientsByQuery(cache.patients, trimmedQuery);
        const queryExtended = trimmedQuery.length > cache.prefix.length;
        if (narrowed.length > 0 || !queryExtended) {
          setDeepSearchResults(narrowed);
          setIsDeepSearching(false);
          setDeepSearchSlow(false);
          setDeepSearchUnavailable(false);
          setDeepSearchError(null);
          return undefined;
        }
      }

      // No cache coverage, no relevant in-flight. If a live fetch exists
      // for an unrelated query, abort it — the user's moved on.
      if (live) {
        live.controller.abort();
        liveFetchRef.current = null;
      }

      // Idle: waiting for the user to click "Search full EHR".
      setDeepSearchResults([]);
      setIsDeepSearching(false);
      setDeepSearchSlow(false);
      setDeepSearchError(null);
      setDeepSearchUnavailable(false);
      // Deliberately keep `deepSearchCompletion` — it auto-expires and
      // should persist briefly across minor keystroke edits.
      return undefined;
    }

    // Manual trigger: fire immediately. Abort any prior in-flight fetch
    // so a re-click always starts fresh.
    if (liveFetchRef.current) {
      liveFetchRef.current.controller.abort();
      liveFetchRef.current = null;
    }

    const controller = new AbortController();
    const dispatchedQuery = trimmedQuery;
    const dispatchedCompanyId = companyId;
    const startedAt = Date.now();
    const thisFetch = {
      companyId: dispatchedCompanyId,
      query: dispatchedQuery,
      controller,
      startedAt,
    };
    liveFetchRef.current = thisFetch;

    setIsDeepSearching(true);
    setDeepSearchSlow(false);
    setDeepSearchError(null);
    setDeepSearchUnavailable(false);
    setDeepSearchCompletion(null);

    logger.info(LogFlow.PatientSearch, 'deep_search_triggered', {
      query_length: dispatchedQuery.length,
      triggered_by: lastDeepTriggerSourceRef.current,
      surface: 'web',
    });

    const slowHintId = setTimeout(() => {
      if (liveFetchRef.current === thisFetch) {
        setDeepSearchSlow(true);
      }
    }, DEEP_SEARCH_SLOW_THRESHOLD_MS);

    // Distinguish "we hit our hard timeout" from "the caller superseded
    // this fetch (new manual click / unrelated query change)". Both go
    // through the same AbortController, but the user only deserves to
    // see an error chip in the timeout case — supersession is silent on
    // purpose because a fresh fetch is already running.
    let timedOut = false;
    const hardTimeoutId = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, DEEP_SEARCH_TIMEOUT_MS);

    // Computed up-front so both the success and failure PostHog captures
    // can include it — knowing what the parser actually dispatched
    // (vs. what the user typed) is the single most useful piece of
    // context when triaging "deep search isn't returning my patient".
    const deepSearchQueries = buildDeepSearchQueries(dispatchedQuery);

    (async () => {
      try {
        const successfulResults: any[] = [];
        let outsideHoursSeen = false;
        const publishDeepSearchPatients = () => {
          if (liveFetchRef.current !== thisFetch) return;
          const patients = dedupePatients(
            successfulResults.flatMap(result => result.patients || []),
          );
          deepCacheRef.current = {
            companyId: dispatchedCompanyId,
            prefix: dispatchedQuery,
            patients,
          };
          const latest = latestQueryRef.current || dispatchedQuery;
          const narrowed = filterPatientsByQuery(patients, latest);
          setDeepSearchResults(narrowed);
          setDeepSearchUnavailable(false);
          setDeepSearchError(null);
        };

        const results = await Promise.allSettled(
          deepSearchQueries.map(queryText =>
            ehrService
              .deepSearchPatients(queryText, DEEP_SEARCH_LIMIT, {
                signal: controller.signal,
              })
              .then(result => {
                if (liveFetchRef.current !== thisFetch) return result;
                if (result?.outsideHours) {
                  outsideHoursSeen = true;
                  return result;
                }
                successfulResults.push(result);
                publishDeepSearchPatients();
                return result;
              }),
          ),
        );
        if (liveFetchRef.current !== thisFetch) return;

        const fulfilled = results
          .filter(result => result.status === 'fulfilled')
          .map(result => result.value);
        if (fulfilled.length === 0 && results.length > 0) {
          throw results.find(result => result.status === 'rejected')?.reason;
        }

        const outsideHours =
          outsideHoursSeen || fulfilled.some(result => result.outsideHours);
        const successful = fulfilled.filter(result => !result.outsideHours);

        if (outsideHours && successful.length === 0) {
          setDeepSearchResults([]);
          setDeepSearchUnavailable(true);
          setDeepSearchCompletion(null);
        } else {
          const patients = dedupePatients(
            successful.flatMap(result => result.patients || []),
          );
          deepCacheRef.current = {
            companyId: dispatchedCompanyId,
            prefix: dispatchedQuery,
            patients,
          };
          // Narrow against whatever the user has typed by now — not the
          // query we originally dispatched. If they kept typing while we
          // were waiting, the latest text is what they actually want.
          const latest = latestQueryRef.current || dispatchedQuery;
          const narrowed = filterPatientsByQuery(patients, latest);

          // Compute "additional" relative to what the user could already
          // see in the cached search panel. We compare against the latest
          // `remotePatients` snapshot rather than the closure'd value so
          // late-arriving cached results don't inflate the additional
          // count.
          const remoteKeys = new Set();
          for (const p of remotePatientsRef.current || []) {
            const key = p.id || p.mrn || p.cacheDocumentId || p.ehrSourceId;
            if (key) remoteKeys.add(key);
          }
          let additionalCount = 0;
          for (const p of narrowed) {
            const key = p.id || p.mrn || p.cacheDocumentId || p.ehrSourceId;
            if (!key || !remoteKeys.has(key)) additionalCount += 1;
          }

          setDeepSearchResults(narrowed);
          setDeepSearchUnavailable(false);
          setDeepSearchCompletion({
            at: Date.now(),
            totalCount: narrowed.length,
            additionalCount,
          });
        }

        try {
          analytics.capture('ehr_deep_search_triggered', {
            query_length: dispatchedQuery.length,
            query_variant_count: deepSearchQueries.length,
            // The actual ECW-shaped strings the parser dispatched. Logged
            // so we can debug "deep search isn't finding X" from
            // PostHog alone — without these, you can't tell the
            // difference between "we asked Ayden the wrong question"
            // and "Ayden returned the wrong answer".
            dispatched_queries: deepSearchQueries,
            results_count: successful.reduce(
              (count, result) => count + (result.patients?.length ?? 0),
              0,
            ),
            duration_ms: Date.now() - startedAt,
            outside_hours: outsideHours,
            triggered_by: lastDeepTriggerSourceRef.current,
            surface: 'web',
          });
        } catch {
          /* posthog is best-effort */
        }
      } catch (error) {
        if (liveFetchRef.current !== thisFetch) return;
        // A user-initiated supersession sets `liveFetchRef.current` to
        // null/the new fetch BEFORE aborting, so the guard above already
        // handles that path. Anything still arriving as an AbortError
        // here is our own hard-timeout fallback firing — don't swallow
        // that, the user clicked the button and deserves to know it
        // didn't pan out.
        if (error?.name === 'AbortError' && !timedOut) return;
        const surfacedError = timedOut
          ? Object.assign(
              new Error(
                'Full EHR search timed out — your EHR took too long to respond.',
              ),
              {name: 'TimeoutError', cause: error},
            )
          : error;
        console.error('Deep EHR search failed:', surfacedError);
        setDeepSearchResults([]);
        setDeepSearchError(surfacedError);
        try {
          analytics.capture('ehr_deep_search_failed', {
            query_length: dispatchedQuery.length,
            query_variant_count: deepSearchQueries.length,
            dispatched_queries: deepSearchQueries,
            duration_ms: Date.now() - startedAt,
            timed_out: timedOut,
            error_name: surfacedError?.name || null,
            error_message: surfacedError?.message || null,
            triggered_by: lastDeepTriggerSourceRef.current,
            surface: 'web',
          });
        } catch {
          /* posthog is best-effort */
        }
      } finally {
        clearTimeout(slowHintId);
        clearTimeout(hardTimeoutId);
        if (liveFetchRef.current === thisFetch) {
          liveFetchRef.current = null;
          setIsDeepSearching(false);
          setDeepSearchSlow(false);
        }
      }
    })();

    // No cleanup: the fetch lifecycle is owned by `liveFetchRef`, not by
    // this effect. We only want to abort on an unrelated query change,
    // which is handled in the non-manual branch above.
    return undefined;
  }, [companyId, hasActiveEhr, searchQuery, manualTriggerTick]);

  const isRemoteSearchActive = Boolean(hasActiveEhr && searchQuery?.trim());

  // Union cached + deep results, deduped by stable identity. The cached
  // search already runs server-side ranking; deep results are appended
  // after so the cache-indexed matches stay on top.
  const ehrPatients = useMemo(() => {
    if (!isRemoteSearchActive) return cachedPatients;
    if (remotePatients.length === 0 && deepSearchResults.length === 0) {
      return remotePatients;
    }

    const byKey = new Map();
    const addPatient = (p: any) => {
      const key = p.id || p.mrn || p.cacheDocumentId || p.ehrSourceId;
      if (!key) {
        byKey.set(`anon_${byKey.size}`, p);
        return;
      }
      if (!byKey.has(key)) byKey.set(key, p);
    };
    for (const p of remotePatients) addPatient(p);
    for (const p of deepSearchResults) addPatient(p);
    return [...byKey.values()];
  }, [cachedPatients, deepSearchResults, isRemoteSearchActive, remotePatients]);

  // Whether the current query resulted in any visible hits — used by the
  // UI to decide whether to show the "Search full EHR" escape hatch.
  const hasAnyResults =
    ehrPatients.length > 0 ||
    (isRemoteSearchActive
      ? remotePatients.length > 0
      : cachedPatients.length > 0);

  // Block the manual "Search full EHR" trigger when the query can't be
  // answered by ECW's Name-mode lookup at all (purely numeric MRN/DOB
  // queries) — those are already covered by the cached path's indexed
  // fields, so firing Ayden would just burn a pool slot for a guaranteed
  // empty result.
  const trimmedForDispatch = searchQuery?.trim() ?? '';
  const hasDispatchableVariant =
    trimmedForDispatch.length >= DEEP_SEARCH_MIN_QUERY_LENGTH &&
    buildDeepSearchQueries(trimmedForDispatch).length > 0;

  const canTriggerManualDeepSearch = Boolean(
    isRemoteSearchActive &&
    companyId &&
    hasDispatchableVariant &&
    !isDeepSearching,
  );

  // ── Auto-trigger deep search on empty cache ────────────────────────
  //
  // The safety net for "I can't find my patient": when the cached search
  // settles with zero hits, fire ONE live EHR search automatically so the
  // user doesn't have to notice and click the manual button. Guard rails so
  // we never stampede Ayden:
  //   - wait for the cached search to finish (`!isSearching`)
  //   - only when the cache truly came back empty (`remotePatients` === 0)
  //   - skip when a deep search is already running or already has results
  //   - only for name-shaped queries ECW can answer (`hasDispatchableVariant`);
  //     pure MRN/DOB are already covered by the indexed cache path
  //   - at most once per distinct query (so re-renders don't re-fire)
  //   - throttled to one dispatch per AUTO_DEEP_SEARCH_MIN_INTERVAL_MS across
  //     queries, so a fast typist blanking the cache repeatedly can't flood
  const autoDeepSearchAtRef = useRef(0);
  const autoDeepSearchedQueryRef = useRef<any>(null);
  useEffect(() => {
    const trimmedQuery = searchQuery?.trim() || '';
    if (!autoDeepSearch) return;
    if (!isRemoteSearchActive || !companyId) return;
    if (isSearching) return;
    if (autoDeepSearchMode !== 'settled' && remotePatients.length > 0) return;
    if (isDeepSearching || deepSearchResults.length > 0) return;
    if (!hasDispatchableVariant) return;
    if (autoDeepSearchedQueryRef.current === trimmedQuery) return;

    const now = Date.now();
    if (now - autoDeepSearchAtRef.current < AUTO_DEEP_SEARCH_MIN_INTERVAL_MS)
      return;

    autoDeepSearchAtRef.current = now;
    autoDeepSearchedQueryRef.current = trimmedQuery;
    triggerDeepSearch('auto');
  }, [
    autoDeepSearch,
    autoDeepSearchMode,
    companyId,
    isRemoteSearchActive,
    isSearching,
    remotePatients,
    isDeepSearching,
    deepSearchResults,
    hasDispatchableVariant,
    searchQuery,
    triggerDeepSearch,
  ]);

  return {
    ehrPatients,
    isSearching,
    searchError,
    isRemoteSearchActive,
    isDeepSearching,
    deepSearchResults,
    deepSearchSlow,
    deepSearchUnavailable,
    deepSearchError,
    deepSearchCompletion,
    hasAnyResults,
    triggerDeepSearch,
    canTriggerManualDeepSearch,
  };
}
